import { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useApolloClient } from '@apollo/client';
import { createScore, useErrorHandler } from '../../apollo/helpers';
import { newestScoreOfType } from '../../utilities/scoring';
import { ScoreType } from '../../models';
import { useEvalConfig } from './hooks';

// Maps judge types (J1, J2, etc.) to their corresponding UTL types
const judgeToUtlType = {
  [ScoreType.J1]: ScoreType.UTL1,
  [ScoreType.J2]: ScoreType.UTL2,
  [ScoreType.J3]: ScoreType.UTL3,
  [ScoreType.J4]: ScoreType.UTL4,
  [ScoreType.J5]: ScoreType.UTL5,
  [ScoreType.J6]: ScoreType.UTL6,
};

// Extract judge number from judge type (J1 -> 1, J2 -> 2, etc.)
const getJudgeNumber = (judgeType) => {
  const match = judgeType?.match(/J(\d)/i);
  return match ? match[1] : null;
};

export default function EvaluatorUtlToggle({
  routineId,
  sessionId,
  scores,
  judgeType,
}) {
  const userId = useSelector((state) => state.user.profile?.id);
  const apolloClient = useApolloClient();
  const [isChecked, setIsChecked] = useState(false);
  const { roles } = useEvalConfig();

  const handleCreateScoreErrors = useErrorHandler(
    'There was a problem submitting the UTL score'
  );

  // Determine the UTL type based on judge role
  const judgeKey = judgeType?.toUpperCase();
  const utlType = judgeToUtlType[judgeKey] || ScoreType.UTL;

  // Check if this judge has UTL permission
  const hasUtlPermission = !roles || !judgeKey || roles[judgeKey]?.[utlType];

  // Sync with existing UTL score from routine
  useEffect(() => {
    const utlScore = newestScoreOfType(scores, utlType);
    setIsChecked(utlScore?.value === 1000);
  }, [scores, utlType]);

  const handleToggle = () => {
    if (!hasUtlPermission) return;

    const newChecked = !isChecked;
    setIsChecked(newChecked);

    const input = {
      value: newChecked ? 1000 : 0, // 1000 = checked (millipoints convention), 0 = unchecked
      routineId,
      sessionId,
      type: utlType,
      userId,
    };

    createScore(apolloClient, input).catch(handleCreateScoreErrors);
  };

  // Don't render if judge doesn't have UTL permission
  if (!hasUtlPermission) {
    return null;
  }

  const judgeNum = getJudgeNumber(judgeType);

  return (
    <Button
      className={`UTL ${isChecked ? 'checked' : ''}`}
      variant="secondary"
      onClick={handleToggle}
    >
      UTL{judgeNum && <sub>{judgeNum}</sub>}
      {isChecked && <div className="lockIndicator" />}
    </Button>
  );
}

// Helper to get combined UTL display string (e.g., "UTL12" if UTL1 and UTL2 are checked)
export function getUtlDisplayString(scores, utlPanel) {
  if (!utlPanel || !scores) return null;

  const checkedIndices = [];
  utlPanel.forEach(({ type }, index) => {
    const score = newestScoreOfType(scores, type);
    if (score?.value === 1000) {
      checkedIndices.push(index + 1); // 1-indexed for display
    }
  });

  if (checkedIndices.length === 0) return null;
  return `UTL${checkedIndices.join('')}`;
}

// Helper to get combined UTL display as JSX with subscript (e.g., UTL₁₂)
export function getUtlDisplayJsx(scores, utlPanel) {
  if (!utlPanel || !scores) return null;

  const checkedIndices = [];
  utlPanel.forEach(({ type }, index) => {
    const score = newestScoreOfType(scores, type);
    if (score?.value === 1000) {
      checkedIndices.push(index + 1);
    }
  });

  if (checkedIndices.length === 0) return null;
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      UTL
      <sub style={{ marginLeft: '1px', fontSize: '0.7em' }}>
        {checkedIndices.join('')}
      </sub>
    </span>
  );
}

// Check if any UTL score is set
export function hasAnyUtlScore(scores, utlPanel) {
  if (!utlPanel || !scores) return false;
  return utlPanel.some(({ type }) => {
    const score = newestScoreOfType(scores, type);
    return score?.value === 1000;
  });
}

export function DisabledUtlToggle({ scores, utlPanel }) {
  // For disabled display, show combined UTL status from all judges
  const hasAnyUtl = hasAnyUtlScore(scores, utlPanel);

  // Fall back to single UTL check if no utlPanel (legacy support)
  if (!utlPanel) {
    const utlScore = newestScoreOfType(scores || [], ScoreType.UTL);
    const isChecked = utlScore?.value === 1000;
    const hasScore = !!utlScore;

    return (
      <Button
        className={`UTL ${isChecked ? 'checked' : ''}`}
        variant="secondary"
        disabled
      >
        UTL
        {hasScore && <div className="lockIndicator" />}
      </Button>
    );
  }

  // Extract judge number from UTL type (UTL1 -> 1, UTL2 -> 2, etc.)
  const utlType = utlPanel[0]?.type;
  const judgeNum = utlType?.match(/UTL(\d)/i)?.[1];

  return (
    <Button
      className={`UTL ${hasAnyUtl ? 'checked' : ''}`}
      variant="secondary"
      disabled
    >
      UTL{judgeNum && <sub>{judgeNum}</sub>}
      {hasAnyUtl && <div className="lockIndicator" />}
    </Button>
  );
}
